export const clone = value => JSON.parse(JSON.stringify(value));
export const fail = (code, message) => Object.assign(new Error(message), { code });
export function sessionKey(lesson, owner) {
  // '|' is escaped inside each segment, so IDs containing '--' cannot collide.
  return [owner, lesson.study_id, lesson.lesson_id, `v${lesson.lesson_version}`].map(encodeURIComponent).join('|');
}
export function initialStageState(stage) {
  switch (stage.type) {
    case 'introduction': return { prediction: '' };
    case 'phonics': return { trial_index: 0, answers: {}, submissions: {}, revisions: 0 };
    case 'word-builder': return { trial_index: 0, words: Object.fromEntries(stage.trials.map(trial => [trial.id, []])), submissions: {}, revisions: 0 };
    case 'matching': return { matches: {}, revisions: 0 };
    case 'reader': return { page_index: 0, page_view_ms: {}, page_visits: { [stage.pages[0].id]: 1 }, answers: {} };
    case 'pack-bag': return { selected: [], checks: 0, checked: false, correct: null, revisions: 0 };
    case 'sorting': return { placements: {}, checks: 0, checked: false, correct: null, revisions: 0 };
    case 'sequence': return { order: stage.scenes.map(scene => scene.id), initial_order: stage.scenes.map(scene => scene.id), moves: 0 };
    default: throw fail('session/config', 'Unsupported activity type.');
  }
}
export function newCheckpoint(lesson, participantId, owner, mode, now = Date.now()) {
  const stage = lesson.stages[0];
  return {
    schema_version: 2, study_id: lesson.study_id, lesson_id: lesson.lesson_id, lesson_version: lesson.lesson_version,
    session_id: sessionKey(lesson, owner), owner_uid: owner, participant_id: participantId,
    data_mode: mode, research_eligible: mode === 'research', revision: 0,
    started_at: new Date(now).toISOString(), updated_at: new Date(now).toISOString(), status: 'in_progress',
    progress: { stage_id: stage.id, stage_index: 0 },
    stage_states: { [stage.id]: initialStageState(stage) },
    stage_records: { [stage.id]: { started_at: new Date(now).toISOString(), elapsed_ms: 0, completed_at: null } },
    total_active_ms: 0, total_clicks: 0, last_operation_id: null
  };
}
export function validateCheckpoint(checkpoint, lesson, participantId, owner, mode) {
  if (checkpoint.schema_version !== 2 || checkpoint.study_id !== lesson.study_id || checkpoint.lesson_id !== lesson.lesson_id || checkpoint.lesson_version !== lesson.lesson_version || checkpoint.session_id !== sessionKey(lesson, owner) || checkpoint.owner_uid !== owner || checkpoint.participant_id !== participantId || checkpoint.data_mode !== mode || checkpoint.research_eligible !== (mode === 'research')) {
    throw fail('session/identity', 'This saved session belongs to a different participant, lesson, or version. Ask your teacher for help.');
  }
  const progress = checkpoint.progress;
  const stage = progress && lesson.stages[progress.stage_index];
  if (!stage || stage.id !== progress.stage_id || !checkpoint.stage_states[stage.id] || !checkpoint.stage_records[stage.id] || !Number.isInteger(checkpoint.revision) || !['in_progress', 'completed'].includes(checkpoint.status)) {
    throw fail('session/invalid', 'The saved session cannot be restored. Ask your teacher for help.');
  }
  return checkpoint;
}
export function stageReady(stage, state) {
  switch (stage.type) {
    case 'introduction': case 'sequence': return true;
    case 'phonics': return stage.trials.every(trial => stage.letters.includes(state.answers[trial.id]));
    case 'word-builder': return stage.trials.every(trial => state.words[trial.id].length === trial.tiles.length);
    case 'matching': return stage.sounds.every(sound => Boolean(state.matches[sound.id]));
    case 'reader': return state.page_index === stage.pages.length - 1 && (stage.questions || []).every(question => Boolean(state.answers[question.id]?.trim()));
    case 'pack-bag': case 'sorting': return state.checked && state.correct;
    default: return false;
  }
}
export function accountTime(checkpoint, lesson, elapsedMs) {
  const stage = lesson.stages[checkpoint.progress.stage_index];
  const delta = Math.max(0, elapsedMs);
  checkpoint.total_active_ms += delta;
  checkpoint.stage_records[stage.id].elapsed_ms += delta;
  if (stage.type === 'reader') {
    const state = checkpoint.stage_states[stage.id];
    const id = stage.pages[state.page_index].id;
    state.page_view_ms[id] = (state.page_view_ms[id] || 0) + delta;
  }
}
// Every interaction passes this reducer. Completed stages cannot be edited.
export function applyAction(checkpoint, lesson, action, now = Date.now()) {
  if (checkpoint.status !== 'in_progress') throw fail('session/completed', 'This session is already complete.');
  const stage = lesson.stages[checkpoint.progress.stage_index];
  const state = checkpoint.stage_states[stage.id];
  const requireType = type => { if (stage.type !== type) throw fail('session/action', 'This action is unavailable here.'); };
  switch (action.type) {
    case 'prediction': requireType('introduction'); state.prediction = String(action.value).slice(0, 1000); break;
    case 'letter_select': {
      requireType('phonics');
      if (!stage.letters.includes(action.letter)) throw fail('session/action', 'Unknown letter.');
      state.answers[stage.trials[state.trial_index].id] = action.letter; state.revisions++; break;
    }
    case 'tile_add': {
      requireType('word-builder');
      const trial = stage.trials[state.trial_index];
      const tiles = state.words[trial.id];
      if (!trial.tiles.some(tile => tile.id === action.tile_id) || tiles.includes(action.tile_id)) throw fail('session/action', 'Unknown or used tile.');
      tiles.push(action.tile_id); state.revisions++; break;
    }
    case 'tile_remove': {
      requireType('word-builder');
      const tiles = state.words[stage.trials[state.trial_index].id];
      if (!Number.isInteger(action.index) || action.index < 0 || action.index >= tiles.length) throw fail('session/action', 'Unknown tile position.');
      tiles.splice(action.index, 1); state.revisions++; break;
    }
    case 'trial_next': {
      if (!['phonics', 'word-builder'].includes(stage.type)) throw fail('session/action', 'Not a trial activity.');
      const trial = stage.trials[state.trial_index];
      const ready = stage.type === 'phonics' ? Boolean(state.answers[trial.id]) : state.words[trial.id].length === trial.tiles.length;
      if (!ready || state.trial_index >= stage.trials.length - 1) throw fail('session/action', 'Finish this item first.');
      state.submissions[trial.id] = { submitted_at: new Date(now).toISOString(), response: stage.type === 'phonics' ? state.answers[trial.id] : state.words[trial.id].map(id => trial.tiles.find(tile => tile.id === id).letter).join('') };
      state.trial_index++; break;
    }
    case 'match': {
      requireType('matching');
      if (!stage.sounds.some(sound => sound.id === action.sound_id) || !stage.words.some(word => word.id === action.word_id)) throw fail('session/action', 'Unknown match.');
      for (const id of Object.keys(state.matches)) if (state.matches[id] === action.word_id) delete state.matches[id];
      state.matches[action.sound_id] = action.word_id; state.revisions++; break;
    }
    case 'page': {
      requireType('reader');
      if (!Number.isInteger(action.index) || Math.abs(action.index - state.page_index) !== 1 || !stage.pages[action.index]) throw fail('session/action', 'Unknown page.');
      state.page_index = action.index;
      const id = stage.pages[action.index].id;
      state.page_visits[id] = (state.page_visits[id] || 0) + 1; break;
    }
    case 'reader_answer': {
      requireType('reader');
      if (!stage.questions.some(question => question.id === action.question_id)) throw fail('session/action', 'Unknown question.');
      state.answers[action.question_id] = String(action.value).slice(0, 2000); break;
    }
    case 'bag_toggle': {
      requireType('pack-bag');
      if (![...stage.supplies, ...stage.distractors].some(item => item.id === action.item_id)) throw fail('session/action', 'Unknown item.');
      const index = state.selected.indexOf(action.item_id);
      if (index < 0) state.selected.push(action.item_id); else state.selected.splice(index, 1);
      state.revisions++; state.checked = false; state.correct = null; break;
    }
    case 'sort_move': {
      requireType('sorting');
      if (!stage.items.some(item => item.id === action.item_id) || ![null, ...stage.zones.map(zone => zone.id)].includes(action.zone_id)) throw fail('session/action', 'Unknown item or destination.');
      if (action.zone_id === null) delete state.placements[action.item_id]; else state.placements[action.item_id] = action.zone_id;
      state.revisions++; state.checked = false; state.correct = null; break;
    }
    case 'check': {
      if (stage.type === 'pack-bag') state.correct = state.selected.length === stage.supplies.length && stage.supplies.every(item => state.selected.includes(item.id));
      else if (stage.type === 'sorting') state.correct = stage.items.every(item => state.placements[item.id] === stage.answer_key[item.id]);
      else throw fail('session/action', 'No answer key for this activity.');
      state.checks++; state.checked = true; break;
    }
    case 'sequence_move': {
      requireType('sequence');
      if (!Number.isInteger(action.index) || ![-1, 1].includes(action.direction) || !state.order[action.index] || !state.order[action.index + action.direction]) throw fail('session/action', 'Unknown scene move.');
      const next = action.index + action.direction;
      [state.order[action.index], state.order[next]] = [state.order[next], state.order[action.index]];
      state.moves++; break;
    }
    case 'stage_complete': {
      const skipped = action.skipped === true;
      if (skipped ? checkpoint.data_mode !== 'demo' : !stageReady(stage, state)) throw fail('session/incomplete', 'Finish this activity first.');
      const record = checkpoint.stage_records[stage.id];
      if (!skipped && ['phonics', 'word-builder'].includes(stage.type)) {
        const trial = stage.trials[state.trial_index];
        state.submissions[trial.id] = { submitted_at: new Date(now).toISOString(), response: stage.type === 'phonics' ? state.answers[trial.id] : state.words[trial.id].map(id => trial.tiles.find(tile => tile.id === id).letter).join('') };
      }
      record.completed_at = new Date(now).toISOString();
      record.completion_kind = skipped ? 'demo_skipped' : checkpoint.data_mode === 'demo' ? 'demo_submitted' : 'submitted';
      record.result = clone(state);
      if (stage.type === 'word-builder') record.result.constructed_words = Object.fromEntries(stage.trials.map(trial => [trial.id, state.words[trial.id].map(id => trial.tiles.find(tile => tile.id === id).letter).join('')]));
      if (checkpoint.progress.stage_index === lesson.stages.length - 1) checkpoint.status = 'completed';
      else {
        const index = checkpoint.progress.stage_index + 1;
        const next = lesson.stages[index];
        checkpoint.progress = { stage_id: next.id, stage_index: index };
        checkpoint.stage_states[next.id] = initialStageState(next);
        checkpoint.stage_records[next.id] = { started_at: new Date(now).toISOString(), elapsed_ms: 0, completed_at: null };
      }
      break;
    }
    case 'session_resume': {
      if (stage.type === 'reader') { const id = stage.pages[state.page_index].id; state.page_visits[id] = (state.page_visits[id] || 0) + 1; }
      break;
    }
    case 'session_start': case 'sound_choose': case 'sort_select': case 'heartbeat': case 'visibility': case 'audio_play': case 'audio_pause': case 'audio_replay': case 'audio_end': case 'audio_error': break;
    default: throw fail('session/action', 'Unknown activity action.');
  }
  checkpoint.updated_at = new Date(now).toISOString();
  return checkpoint;
}
